import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, formatEUR, formatDate } from "@/components/app/Shared";
import { ArrowLeft, Download, Send, MessageCircle, Pencil, X, Save, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, showPrices } = useAuth();
  const isAdmin = ["admin", "organization_admin"].includes(user?.role);
  const canManageOrder = isAdmin || user?.role === "chef";

  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draftItems, setDraftItems] = useState([]); // [{product_id, quantity, product_name, unit, price}]
  const [addPid, setAddPid] = useState("");

  const load = () => api.get(`/orders/${id}`).then((r) => {
    setOrder(r.data);
    setDraftItems(r.data.items.map((it) => ({ ...it })));
  });
  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (canManageOrder) api.get("/products").then((r) => setProducts(r.data)); }, [canManageOrder]);

  const draftTotal = draftItems.reduce((s, it) => s + (it.price || 0) * (it.quantity || 0), 0);

  const startEdit = () => {
    setDraftItems(order.items.map((it) => ({ ...it })));
    setEditing(true);
  };
  const cancelEdit = () => {
    setDraftItems(order.items.map((it) => ({ ...it })));
    setEditing(false);
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/orders/${order.id}`, {
        restaurant_id: order.restaurant_id,
        items: draftItems.map((it) => ({ product_id: it.product_id, quantity: parseFloat(it.quantity) || 0 })),
        notes: order.notes,
      });
      toast.success("Bestelling bijgewerkt");
      setEditing(false);
      load();
    } catch { toast.error("Opslaan mislukt"); }
  };

  const updateQty = (pid, qty) => setDraftItems((arr) => arr.map((it) => it.product_id === pid ? { ...it, quantity: qty } : it));
  const removeItem = (pid) => setDraftItems((arr) => arr.filter((it) => it.product_id !== pid));
  const addItem = () => {
    if (!addPid) return;
    const p = products.find((x) => x.id === addPid);
    if (!p) return;
    if (draftItems.find((it) => it.product_id === p.id)) { toast.message("Al toegevoegd"); return; }
    setDraftItems((arr) => [...arr, { product_id: p.id, product_name: p.name, unit: p.unit, price: p.price, quantity: 1 }]);
    setAddPid("");
  };

  const downloadPDF = async () => {
    try {
      const res = await api.get(`/orders/${order.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `stockly-bestelling-${order.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download mislukt"); }
  };

  const forward = async () => {
    if (!confirm("Bestelling doorsturen naar leveranciers via e-mail?")) return;
    try {
      const { data } = await api.post(`/orders/${order.id}/forward`, {});
      if (data.sent_to?.length) toast.success(`Verzonden naar ${data.sent_to.length} ontvanger(s)`);
      else toast.message("Geen leveranciers met e-mail gevonden");
    } catch { toast.error("Doorsturen mislukt"); }
  };

  const shareWA = () => {
    const lines = order.items.map((it) => `• ${it.product_name} — ${it.quantity} ${it.unit}`).join("\n");
    const text = `Bestelling ${order.restaurant_name}\n\n${lines}${showPrices ? `\n\nTotaal: ${formatEUR(order.total)}` : ""}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const [confirmDelete, setConfirmDelete] = useState(false);

  const removeOrder = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 3500);
      return;
    }
    try {
      await api.delete(`/orders/${order.id}`);
      toast.success("Bestelling verwijderd");
      nav("/bestellingen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  if (!order) return <div className="p-10 text-zinc-500">Laden…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px] mx-auto" data-testid="order-detail-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4" data-testid="back-button">
        <ArrowLeft size={15} /> Terug
      </button>

      <PageHeader
        overline={`Bestelling ${order.readable_id || `#${order.id.slice(0, 8)}`}`}
        title={order.restaurant_name}
        description={`Geplaatst door ${order.chef_name} op ${formatDate(order.created_at)}`}
        actions={
          canManageOrder && !editing && (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button onClick={startEdit} className="inline-flex items-center gap-2 h-9 px-3 border border-zinc-300 hover:border-[#1B2D5C] text-sm rounded-md text-zinc-700 hover:text-[#1B2D5C]" data-testid="edit-order-button">
                <Pencil size={14} /> Bewerken
              </button>
              {isAdmin && (
                <button onClick={forward} className="inline-flex items-center gap-2 h-9 px-3 bg-[#5BC044] hover:bg-[#4FAA3A] text-white text-sm rounded-md font-semibold" data-testid="forward-supplier-button">
                  <Send size={14} /> Doorsturen
                </button>
              )}
              <button onClick={shareWA} className="inline-flex items-center gap-2 h-9 px-3 bg-[#25D366] hover:bg-[#1FA855] text-white text-sm rounded-md font-semibold" data-testid="whatsapp-share-button">
                <MessageCircle size={14} /> WhatsApp
              </button>
              <button onClick={downloadPDF} className="inline-flex items-center gap-2 h-9 px-3 border border-zinc-300 hover:border-[#1B2D5C] text-sm rounded-md text-zinc-700 hover:text-[#1B2D5C]" data-testid="download-pdf-button">
                <Download size={14} /> PDF
              </button>
              <button onClick={removeOrder} className={`inline-flex items-center gap-2 h-9 px-3 text-sm rounded-md font-semibold ${confirmDelete ? "bg-red-600 hover:bg-red-700 text-white" : "border border-red-200 hover:bg-red-50 hover:border-red-400 text-red-600"}`} data-testid="delete-order-button">
                <Trash2 size={14} /> {confirmDelete ? "Klik nogmaals om te bevestigen" : "Verwijderen"}
              </button>
            </div>
          )
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-flat p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Producten</div>
            {editing && (
              <div className="flex gap-2">
                <button onClick={cancelEdit} className="inline-flex items-center gap-1 h-8 px-3 border border-zinc-300 text-sm rounded-md text-zinc-700" data-testid="cancel-edit-button">
                  <X size={13} /> Annuleren
                </button>
                <button onClick={saveEdit} className="inline-flex items-center gap-1 h-8 px-3 bg-[#5BC044] hover:bg-[#4FAA3A] text-white text-sm rounded-md font-semibold" data-testid="save-edit-button">
                  <Save size={13} /> Opslaan
                </button>
              </div>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-200">
              <tr className="text-left text-zinc-500">
                <th className="py-2 font-medium">Product</th>
                <th className="py-2 font-medium text-right">Aantal</th>
                {showPrices && <th className="py-2 font-medium text-right">Prijs</th>}
                {showPrices && <th className="py-2 font-medium text-right">Subtotaal</th>}
                {editing && <th className="py-2 w-10"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {(editing ? draftItems : order.items).map((it) => (
                <tr key={it.product_id} data-testid={`order-item-${it.product_id}`}>
                  <td className="py-3">
                    <div className="font-medium">{it.product_name}</div>
                    <div className="text-xs text-zinc-500">per {it.unit}</div>
                  </td>
                  <td className="py-3 text-right font-mono">
                    {editing ? (
                      <input type="number" min="0" step="0.1" value={it.quantity}
                        onChange={(e) => updateQty(it.product_id, e.target.value)}
                        className="w-20 h-8 px-2 border border-zinc-300 rounded text-right text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45"
                        data-testid={`qty-input-${it.product_id}`}
                      />
                    ) : (
                      `${it.quantity} ${it.unit}`
                    )}
                  </td>
                  {showPrices && <td className="py-3 text-right font-mono">{formatEUR(it.price)}</td>}
                  {showPrices && <td className="py-3 text-right font-mono font-semibold">{formatEUR((it.price || 0) * (it.quantity || 0))}</td>}
                  {editing && (
                    <td className="py-3 text-right">
                      <button onClick={() => removeItem(it.product_id)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`remove-item-${it.product_id}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {showPrices && (
              <tfoot>
                <tr className="border-t-2 border-zinc-900">
                  <td colSpan={editing ? 4 : 3} className="py-3 text-right overline">Totaal</td>
                  <td className="py-3 text-right font-mono text-lg font-bold">{formatEUR(editing ? draftTotal : order.total)}</td>
                  {editing && <td />}
                </tr>
              </tfoot>
            )}
          </table>

          {editing && (
            <div className="mt-4 pt-4 border-t border-zinc-100 flex gap-2 items-end">
              <div className="flex-1">
                <label className="overline block mb-1.5">Product toevoegen</label>
                <select value={addPid} onChange={(e) => setAddPid(e.target.value)}
                  className="w-full h-9 px-3 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45"
                  data-testid="add-product-select"
                >
                  <option value="">— Kies een product —</option>
                  {products.filter((p) => !draftItems.find((it) => it.product_id === p.id)).map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>
                  ))}
                </select>
              </div>
              <button onClick={addItem} disabled={!addPid} className="h-9 px-3 bg-[#1B2D5C] hover:bg-[#142244] disabled:opacity-50 text-white rounded-md text-sm font-semibold inline-flex items-center gap-1.5" data-testid="add-item-button">
                <Plus size={14} /> Toevoegen
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="card-flat p-6" data-testid="order-info-card">
            <div className="overline mb-3">Notities</div>
            <p className="text-sm text-zinc-700">{order.notes || <span className="text-zinc-400 italic">Geen notities</span>}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
